import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Building2, Plus } from 'lucide-react';
import { Input } from './input.jsx';
import { Button } from './button.jsx';
import { Label } from './label.jsx';
import { cn } from '../../lib/utils';
import { searchCustomerOrganisations } from '../../services/api';

const ROLE_COLOURS = {
  customer: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  supplier: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  subcontractor: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
};

function RoleBadge({ role }) {
  const colour = ROLE_COLOURS[role] || 'bg-muted text-muted-foreground';
  return (
    <span className={cn('inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium capitalize', colour)}>
      {role}
    </span>
  );
}

/**
 * OrganisationLookup - Searchable dropdown for selecting customer organisations
 *
 * Features:
 * - Debounced server-side search
 * - Rich result items with role badges and address
 * - "+ Create New Organisation" button at bottom
 * - Keyboard navigation
 * - Click outside to close
 */
const OrganisationLookup = ({
  label,
  value,
  onChange,
  onCreateNew,
  placeholder = 'Search for an organisation...',
  required = false,
  disabled = false,
  error,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync display text when value changes from parent
  useEffect(() => {
    if (value && typeof value === 'object') {
      setSearchTerm(value.name || '');
    } else if (!value) {
      setSearchTerm('');
    }
  }, [value]);

  // Debounced search
  const doSearch = useCallback(async (term) => {
    if (!term || term.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const docs = await searchCustomerOrganisations(term);
      setResults(docs);
    } catch (err) {
      console.error('Organisation search failed:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(searchTerm), 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchTerm, doSearch]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        inputRef.current && !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setHighlightIndex(-1);
    setIsOpen(true);
  };

  const handleSelect = (org) => {
    setSearchTerm(org.name);
    setIsOpen(false);
    onChange(org);
  };

  const handleClear = () => {
    setSearchTerm('');
    setResults([]);
    setIsOpen(false);
    onChange(null);
    inputRef.current?.focus();
  };

  const handleCreateNew = () => {
    setIsOpen(false);
    if (onCreateNew) {
      onCreateNew(searchTerm);
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        return;
      }
      return;
    }

    const totalItems = results.length + (onCreateNew ? 1 : 0);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex((prev) => (prev + 1) % totalItems);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex((prev) => (prev - 1 + totalItems) % totalItems);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightIndex >= 0 && highlightIndex < results.length) {
          handleSelect(results[highlightIndex]);
        } else if (highlightIndex === results.length && onCreateNew) {
          handleCreateNew();
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightIndex(-1);
        break;
      default:
        break;
    }
  };

  const parseRoles = (rolesStr) => {
    if (!rolesStr) return [];
    return rolesStr.split(',').map((r) => r.trim()).filter(Boolean);
  };

  return (
    <div className={cn('relative', className)}>
      {label && (
        <Label className="block font-medium text-xs mb-1">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      <div className="relative">
        <div className="relative flex items-center">
          <Input
            ref={inputRef}
            type="text"
            className={cn(
              'pr-16 h-8 text-sm',
              error && 'border-destructive focus-visible:ring-destructive',
              disabled && 'cursor-not-allowed opacity-50'
            )}
            value={searchTerm}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (searchTerm.trim().length >= 2) setIsOpen(true);
            }}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
          />

          {searchTerm && !disabled && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-7 h-6 w-6 hover:bg-accent text-muted-foreground hover:text-foreground"
              onMouseDown={(e) => {
                e.preventDefault();
                handleClear();
              }}
              title="Clear"
              tabIndex={-1}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}

          <Search className="absolute right-2 h-4 w-4 text-muted-foreground pointer-events-none" />
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div
            ref={dropdownRef}
            className="absolute z-[70] mt-1 w-full rounded-md border bg-popover shadow-md max-h-80 overflow-y-auto"
          >
            {loading && (
              <div className="flex items-center justify-center py-4">
                <div className="h-4 w-4 border-2 border-muted border-t-primary rounded-full animate-spin" />
                <span className="ml-2 text-xs text-muted-foreground">Searching...</span>
              </div>
            )}

            {!loading && searchTerm.trim().length >= 2 && results.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                No matching organisations found
              </div>
            )}

            {!loading && searchTerm.trim().length < 2 && (
              <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                Type at least 2 characters to search
              </div>
            )}

            {results.map((org, idx) => (
              <div
                key={org.$id}
                className={cn(
                  'px-3 py-2 cursor-pointer transition-colors border-b last:border-b-0',
                  highlightIndex === idx
                    ? 'bg-accent text-accent-foreground'
                    : 'hover:bg-accent/50'
                )}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(org);
                }}
                onMouseEnter={() => setHighlightIndex(idx)}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{org.name}</div>
                      {org.tradingName && (
                        <div className="text-xs text-muted-foreground truncate">
                          t/a {org.tradingName}
                        </div>
                      )}
                      {(org.suburb || org.region) && (
                        <div className="text-xs text-muted-foreground truncate">
                          {[org.suburb, org.region].filter(Boolean).join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {parseRoles(org.roles).map((role) => (
                      <RoleBadge key={role} role={role} />
                    ))}
                  </div>
                </div>
              </div>
            ))}

            {/* Create New button */}
            {onCreateNew && (
              <>
                {results.length > 0 && <div className="border-t" />}
                <div
                  className={cn(
                    'px-3 py-2.5 cursor-pointer transition-colors flex items-center gap-2 text-primary',
                    highlightIndex === results.length
                      ? 'bg-accent text-accent-foreground'
                      : 'hover:bg-accent/50'
                  )}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleCreateNew();
                  }}
                  onMouseEnter={() => setHighlightIndex(results.length)}
                >
                  <Plus className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    Create New Organisation{searchTerm.trim() ? `: "${searchTerm.trim()}"` : ''}
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="text-xs text-destructive mt-1">{error}</p>
      )}
    </div>
  );
};

export default OrganisationLookup;
